import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Send,
  Zap,
  LayoutGrid,
  CalendarDays,
  Users,
  MapPin,
  ChevronRight,
  MessageCircle,
  PartyPopper,
  UsersRound,
  X,
  LogIn,
} from "lucide-react";
import QuizModal from "@/components/QuizModal";
import DanoneFooter from "@/components/danone/DanoneFooter";
import DanoneEventTypes from "@/components/danone/DanoneEventTypes";
import DanoneServices from "@/components/danone/DanoneServices";
import DanonePreferredTerms from "@/components/danone/DanonePreferredTerms";
import DanoneHowTo from "@/components/danone/DanoneHowTo";
import { withUtm } from "@/components/danone/utm";
import CeliaContactPopover from "@/components/danone/CeliaContactPopover";

const NABOO_LOGO = "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/logo.naboo.email.png";
const DANONE_LOGO = "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Logos/Decathlon-Logo.png";
const CELIA_PHOTO = "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Logos/celia_100x100.jpg";
const HERO_BG = "/lovable-uploads/villa-st-tropez-quiz-bg.webp";

const CLIENT_URL = "https://decathlon.naboo.app";

const venues = [
  {
    name: "Kasteel van Ordingen",
    location: "Belgique",
    specs: "",
    image: "/lovable-uploads/5af6d75b-bc82-4752-9ead-ca776ea7c089.png",
    url: "https://www.naboo.app/fr-fr/explorer/houses/kasteel-van-ordingen",
  },
  {
    name: "El Rincón",
    location: "Espagne",
    specs: "",
    image: "/lovable-uploads/84e950ac-1e48-42aa-b2bc-e416dd561550.png",
    url: "https://www.naboo.app/fr-fr/explorer/houses/el-rincon",
  },
  {
    name: "Domaine de Maffliers",
    location: "France",
    specs: "",
    image: "/lovable-uploads/a9e5bd65-c5b7-4751-8a18-f5e362c61240.png",
    url: "https://www.naboo.app/fr-fr/explorer/houses/63ea5494a135ab9ff7583bf8",
  },
  {
    name: "Port Lympne Hotel",
    location: "Royaume-Uni",
    specs: "",
    image: "/lovable-uploads/ade02424-d588-4166-a844-571b21592735.png",
    url: "https://www.naboo.app/fr-fr/explorer/houses/67067852dfe9d4d3c7d6f005",
  },
];

const stats = [
  { value: "53", label: "Événements", green: false, icon: CalendarDays },
  { value: "5 009", label: "Participants", green: true, icon: UsersRound },
  { value: "197", label: "Collègues inscrits", green: false, icon: Users },
  { value: "0 €", label: "Frais de service", green: true, icon: null },
];

const ext = { target: "_blank", rel: "noopener noreferrer" } as const;

const Danone = () => {
  const [showFloating, setShowFloating] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [pushClosed, setPushClosed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowFloating(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="min-h-screen bg-white text-[#111111] overflow-x-hidden pt-16 sm:pt-20"
      style={{ scrollBehavior: "smooth" }}
    >
      <Helmet>
        <title>Naboo × Decathlon — Plateforme événementielle dédiée aux collaborateurs Decathlon</title>
        <meta
          name="description"
          content="Plateforme Naboo réservée exclusivement aux collaborateurs Decathlon : organisez vos séminaires, événements et team building avec des conditions négociées et un accompagnement dédié par Célia."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://decathlon.naboo.app/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Naboo × Decathlon" />
        <meta
          property="og:title"
          content="Naboo × Decathlon — Plateforme événementielle dédiée aux collaborateurs Decathlon"
        />
        <meta
          property="og:description"
          content="Espace privé Decathlon × Naboo : organisez vos séminaires et événements d'entreprise avec tarifs négociés et accompagnement dédié. Réservé aux équipes Decathlon."
        />
        <meta property="og:url" content="https://decathlon.naboo.app/" />
        <meta property="og:locale" content="fr_FR" />
        <meta name="theme-color" content="#C6E278" />
      </Helmet>

      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-3 lg:gap-4">
          <div className="flex items-center gap-1.5 sm:gap-3 lg:gap-4 flex-shrink-0 min-w-0">
            <img src={DANONE_LOGO} alt="Decathlon" className="h-10 sm:h-14 w-auto object-contain block" />
            <div className="h-6 sm:h-8 w-px bg-black/15" />
            <a href={withUtm("https://www.naboo.app/")} {...ext} className="inline-flex items-center">
              <img src={NABOO_LOGO} alt="Naboo" className="h-6 sm:h-8 w-auto object-contain block" />
            </a>
          </div>

          {/* Center: Célia pill (desktop only) */}
          <CeliaContactPopover align="center">
            <button
              type="button"
              aria-label="Discuter avec Célia"
              className="hidden lg:flex items-center gap-3 bg-white border border-black/10 rounded-full pl-1.5 pr-2 py-1.5 shadow-sm hover:shadow-md transition-shadow group"
            >
              <img src={CELIA_PHOTO} alt="Célia" className="w-9 h-9 rounded-full object-cover" />
              <span className="text-sm text-[#111111]">
                <span className="font-bold">Célia</span>
                <span className="text-[#6b7280]">, votre conseillère </span>
                <span className="font-bold">DECATHLON</span>
              </span>
              <span className="inline-flex items-center gap-1.5 bg-[#111111] text-white text-xs font-semibold px-3 py-1.5 rounded-full ml-1 group-hover:opacity-90 transition-opacity">
                <MessageCircle className="w-3.5 h-3.5" />
                Discuter
              </span>
            </button>
          </CeliaContactPopover>

          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
            <CeliaContactPopover align="end">
              <button
                type="button"
                aria-label="Discuter avec Célia"
                className="lg:hidden inline-flex items-center gap-1 sm:gap-1.5 bg-white text-[#111111] text-xs font-semibold px-1.5 py-1.5 sm:px-3 sm:py-2.5 rounded-full border border-black/15 hover:border-black/40 hover:bg-black/5 transition-colors"
              >
                <img src={CELIA_PHOTO} alt="" className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover" />
                <span className="hidden sm:inline">Discuter</span>
              </button>
            </CeliaContactPopover>

            <button
              type="button"
              onClick={() => setQuizOpen(true)}
              className="inline-flex items-center gap-1 sm:gap-2 bg-[#C6E278] text-[#111111] text-xs sm:text-sm font-semibold px-2.5 py-1.5 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#C6E278]" />
              <span className="hidden xs:inline sm:hidden">Demande</span>
              <span className="hidden sm:inline">Envoyer ma demande</span>
            </button>

            <a
              href={withUtm(CLIENT_URL)}
              {...ext}
              aria-label="Accéder à la plateforme Decathlon"
              className="inline-flex items-center gap-1.5 sm:gap-2 bg-white text-[#111111] text-xs sm:text-sm font-semibold px-1.5 py-1.5 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 rounded-full border border-black/15 hover:border-black/40 hover:bg-black/5 transition-colors"
            >
              <LogIn className="w-3.5 h-3.5 sm:hidden" />
              <span className="hidden sm:inline lg:hidden">Plateforme</span>
              <span className="hidden lg:inline">Accéder à la plateforme Decathlon</span>
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative bg-[#0a0f1e]"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-14">
          <div className="max-w-4xl mx-auto text-center text-white">
            <span className="inline-flex items-center gap-2 bg-[#C6E278] text-[#111111] text-xs font-bold uppercase tracking-wider px-5 py-2 rounded-full mb-5">
              <Zap className="w-4 h-4 fill-[#111111]" />
              Quiz
            </span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight mb-6 max-w-3xl mx-auto">
              Quelques questions rapides pour trouver votre lieu idéal.
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 mb-6 text-white">
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <LayoutGrid className="w-5 h-5 text-[#C6E278]" />
                Type d'événement
              </span>
              <ChevronRight className="w-4 h-4 text-[#C6E278]" />
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <CalendarDays className="w-5 h-5 text-[#C6E278]" />
                Dates
              </span>
              <ChevronRight className="w-4 h-4 text-[#C6E278]" />
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-5 h-5 text-[#C6E278]" />
                Participants
              </span>
              <ChevronRight className="w-4 h-4 text-[#C6E278]" />
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <MapPin className="w-5 h-5 text-[#C6E278]" />
                Votre match
              </span>
            </div>

            <button
              type="button"
              onClick={() => setQuizOpen(true)}
              className="inline-flex items-center gap-2 bg-[#C6E278] text-[#111111] text-sm font-semibold px-6 py-3 rounded-full hover:opacity-90 hover:scale-105 transition-all"
            >
              Démarrer →
            </button>

            <p className="text-sm text-white/80 mt-4">Résultats en moins de 30 sec</p>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4 divide-y lg:divide-y-0 lg:divide-x divide-black/10">
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="px-4 lg:px-6 text-center first:pt-0 [&:nth-child(2)]:pt-0 pt-6 lg:pt-0">
                  <div className="flex items-center justify-center gap-2">
                    {Icon && (
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: s.green ? "#a8c45e" : "#111111" }} />
                    )}
                    <div
                      className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
                      style={{ color: s.green ? "#a8c45e" : "#111111" }}
                    >
                      {s.value}
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-[#6b7280] mt-1">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-3 sm:pt-4 sm:pb-4">
          <div className="w-full bg-gradient-to-br from-[#fafbf5] to-[#f0f3e8] rounded-2xl px-5 sm:px-6 py-3 sm:py-3.5 border border-black/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base sm:text-lg font-bold leading-tight text-[#111111]">
                197 de vos collègues utilisent déjà Naboo
              </h2>
              <p className="text-xs sm:text-sm text-[#6b7280] mt-0.5">
                Rejoignez les équipes Decathlon qui organisent leurs événements via la plateforme dédiée.
              </p>
            </div>
            <a
              href={withUtm(CLIENT_URL)}
              {...ext}
              className="inline-flex items-center gap-2 bg-[#111111] text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity flex-shrink-0"
            >
              Accéder à la plateforme Decathlon
            </a>
          </div>
        </div>
      </section>

      <DanoneEventTypes />
      <DanoneServices />
      <DanonePreferredTerms />
      <DanoneHowTo />

      {/* TOP DESTINATIONS DANONE */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6 sm:pt-6 sm:pb-8">
          <div className="mb-4 text-center">
            <span className="inline-block bg-[#C6E278] text-[#111111] text-xs uppercase tracking-wider font-semibold px-3 py-1 rounded-full mb-4">
              Plébiscités par vos collègues
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#111111]">Top destinations</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {venues.map((v) => (
              <a
                key={v.name}
                href={withUtm(v.url)}
                {...ext}
                className="group bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.name}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-[#111111] text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                    <Zap className="w-3.5 h-3.5 fill-[#C6E278] text-[#C6E278]" />
                    Réservation instantanée
                  </span>
                </div>
                <div className="p-4">
                  <div className="font-semibold text-[#111111] mb-1 line-clamp-1">{v.name}</div>
                  <div className="text-sm text-[#6b7280] mb-2 line-clamp-1">{v.location}</div>
                  <div className="text-xs text-[#6b7280] line-clamp-1">{v.specs}</div>
                </div>
              </a>
            ))}
          </div>

          <a
            href={withUtm("https://fr.ext.naboo.app/")}
            {...ext}
            className="group mt-5 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-br from-[#fafbf5] to-[#f0f3e8] rounded-2xl border border-black/5 px-6 sm:px-8 py-4 sm:py-5 hover:shadow-md transition-shadow"
          >
            <div className="text-center sm:text-left">
              <div className="text-lg sm:text-xl font-bold text-[#111111] leading-tight">
                Découvrez plus de 50 000 lieux et prestataires dans notre base de données
              </div>
              <div className="text-sm text-[#6b7280] mt-1">
                Explorez l'ensemble du catalogue Naboo réservé aux équipes Decathlon.
              </div>
            </div>
            <span className="inline-flex items-center gap-2 bg-[#111111] text-white text-sm font-semibold px-6 py-3 rounded-full group-hover:opacity-90 transition-opacity flex-shrink-0">
              Explorer les lieux →
            </span>
          </a>
        </div>
      </section>

      <DanoneFooter />

      {showFloating && !pushClosed && (
        <div
          onClick={() => setQuizOpen(true)}
          className="fixed z-40 left-0 top-[68px] md:top-[25%] cursor-pointer hover:opacity-90 transition-all duration-500 ease-in-out"
        >
          <div className="relative flex items-center gap-1.5 md:gap-2 shadow-lg px-2 py-1.5 pr-6 md:px-3 md:py-2 md:pr-7 rounded-r-xl md:rounded-r-2xl bg-[#c6e278]">
            <Zap className="w-3 h-3 md:w-4 md:h-4 text-gray-800 flex-shrink-0 fill-current animate-bounce" />
            <p className="text-gray-800 text-[10px] md:text-xs leading-tight font-bold">
              Votre lieu idéal en 1 mn
              <br />
              <span className="font-bold">Quiz Naboo</span>
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPushClosed(true);
              }}
              className="absolute right-0.5 md:right-1 top-1/2 -translate-y-1/2 p-0.5 md:p-1 hover:bg-black/10 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-800" />
            </button>
          </div>
        </div>
      )}

      <QuizModal isOpen={quizOpen} onClose={() => setQuizOpen(false)} />
    </div>
  );
};

export default Danone;
